<template>
  <div class="payouts-to-card-table-item">
    <transaction-table-item
      ref="transactionItem"
      :transaction="transaction"
      :details-blocks="transaction.TRANSACTION_DETAIL_BLOCKS"
      @actionFired="handleActionFired"
    >
      <template
        #shortInfo="{
          hideActionsMenu,
          toggleActionsMenu,
          isShowActionsMenu,
          makeAction,
          actionsPending,
        }"
      >
        <div class="transaction-table-item__col transaction-table-item__date">
          <div class="transaction-table-item__date-main">
            {{ transaction.transactionDate }}
          </div>
          <div class="transaction-table-item__date-time">
            {{ transaction.transactionTime }}
          </div>
        </div>
        <div class="transaction-table-item__col transaction-table-item__pt-id">
          {{ transaction.cardNumber }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__pt-id">
          {{ transaction.partnerTransactionId }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__op-id">
          {{ transaction.operationId }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__client">
          <div class="transaction-table-item__client-full-name">
            {{ transaction.userFullName }}
          </div>
          <div class="transaction-table-item__client-phone">
            {{ transaction.userPhone }}
          </div>
          <div class="transaction-table-item__client-email">
            {{ transaction.userEmail }}
          </div>
        </div>
        <div class="transaction-table-item__col transaction-table-item__amount">
          {{ transaction.transactionAmount }}
          {{ transaction.transactionCurrency }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__status">
          <transaction-status
            :status-id="transaction.operationStatus"
          ></transaction-status>
        </div>
        <div
          class="transaction-table-item__col transaction-table-item__actions"
          v-click-outside="hideActionsMenu"
        >
          <div
            class="transaction-table-item__actions-btn"
            @click="toggleActionsMenu"
          >
            <svg
              width="20"
              height="4"
              viewBox="0 0 20 4"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="2" cy="2" r="2" fill="#B9C4D8" />
              <circle cx="18" cy="2" r="2" fill="#B9C4D8" />
              <circle cx="10" cy="2" r="2" fill="#B9C4D8" />
            </svg>
          </div>
          <div
            class="transaction-table-item__actions-menu"
            v-if="isShowActionsMenu"
          >
            <template v-if="itemActions.length > 0">
              <div
                class="transaction-table-item__actions-menu-item"
                v-for="action in itemActions"
                :class="{ disabled: actionsPending.includes(action.id) }"
                @click.stop="makeAction(action)"
              >
                {{ action.title }}
              </div>
            </template>
            <template v-else>
              <div class="transaction-table-item__actions-no-actions">
                {{ $t('transaction-actions.no-available-actions') }}
              </div>
            </template>
          </div>
        </div>
      </template>
    </transaction-table-item>
    <transaction-receipt-popup
      :transaction="transaction"
      :pdf-blob="transactionReceiptBlob"
      ref="transactionReceiptPopup"
    ></transaction-receipt-popup>
  </div>
</template>

<script>
import TransactionTableItem from '@vue-app/components/company/transactions/table/TransactionsTableItem.vue'
import { Transaction } from '@js-app/entities/transaction/Transaction'
import TransactionStatus from '@vue-app/components/transaction/TransactionStatus.vue'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import TransactionReceiptPopup from '@vue-app/components/company/transactions/popup/TransactionReceiptPopup.vue'
import { RouteHelper } from '@js-app/services/helpers/routes/routes-helper'
import { mapState } from 'vuex'
import { SupportService } from '@js-app/services/support/support-service'

export default {
  name: 'PayoutsToCardTableItem',
  components: {
    TransactionReceiptPopup,
    TransactionStatus,
    TransactionTableItem,
  },
  props: {
    transaction: {
      type: Transaction,
    },
  },
  data: () => ({
    transactionReceiptBlob: null,
  }),

  computed: {
    tr() {
      return tr
    },
    ...mapState(['selectedCompany']),
    itemActions() {
      let _actions = []

      if (this.transaction.canGetReceipt) {
        _actions.push({
          id: 'get-receipt',
          title: this.$t('transaction-actions.get-receipt'),
          handler: this.loadReceipt,
        })
      }

      _actions.push({
        id: 'payout-to-card-details',
        title: this.$t('transaction-actions.payout-to-card-details'),
        handler: this.toPayoutToCardDetails,
      })

      _actions.push({
        id: 'support-tg-chat',
        title: this.$t('transaction-actions.support-tg-chat'),
        handler: SupportService.goToTelegramChat,
      })

      return _actions
    },
  },

  methods: {
    async handleActionFired(action) {
      try {
        await action.handler()
      } catch (e) {
        console.error(e)
      }

      try {
        this.$refs.transactionItem.actionFinished(action.id)
      } catch (e) {}
    },
    async loadReceipt() {
      let receiptResponse = await CompaniesApi.getBillReceipt(
        this.selectedCompany,
        this.transaction.operationId
      )
      this.transactionReceiptBlob = URL.createObjectURL(receiptResponse.data)
      return this.$refs.transactionReceiptPopup.open()
    },
    toPayoutToCardDetails() {
      this.$router.push(
        RouteHelper.getRoute('payout-to-card-view', {
          companyId: this.selectedCompany,
          payoutId: this.transaction.operationId,
        })
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.payouts-to-card-table-item {
  .transaction-table-item {
    &__phone-email {
      word-break: break-all;
    }

    &__type {
      display: flex;
      align-items: center;
      justify-content: center;

      position: relative;

      &-refund {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    &__id {
      word-break: break-all;
    }

    &__amount {
      font-weight: 600;
      font-size: 14px;
      line-height: 18px;
      color: #1a1f29;
      opacity: 0.7;
      justify-content: flex-end;
    }

    &__client {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      &-full-name {
        color: #1a1f29;

        &:empty {
          display: none;
        }
      }

      &-phone,
      &-email {
        color: #27365b;
        word-break: break-all;

        &:empty {
          display: none;
        }
      }
    }

    &__currency {
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
      color: rgba(#585f6d, 0.7);
    }

    &__status {
      align-self: center;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-left: 20px;
      padding-right: 20px;

      &-item {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 105px;
        max-width: 100%;
        height: 28px;
        border-radius: 6px;
        font-weight: 500;
        font-size: 12px;
        line-height: 28px;

        &--paid {
          background: rgba(39, 204, 36, 0.2);
          color: #44d941;
        }
      }
    }

    &__card-number {
      word-break: break-all;
    }
  }
}
</style>
